import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';

export default function useLicenseKeyAssignEditor(licenseKey?: LicenseKeyModel) {
    const { t } = useTranslation();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const { handleLoadingDataChange, loadingData, createManagedOrg } = OrganizationContainer.useContainer();
    const [licenseKeyAssign, setLicenseKeyAssign] = useState<LicenseKeyModel | undefined>(licenseKey);

    const [assignKey, setAssignKey] = useState<NewOrganizationWithKeyRequest>({
        name: '',
        description: '',
        key: licenseKey?.accessCode ?? '',
        customBillingUrl: '',
    });

    const resetAssignKey = () => {
        setAssignKey({
            name: '',
            description: '',
            key: licenseKey?.accessCode ?? '',
            customBillingUrl: '',
        });
    };

    const handleInputChange = (field: string, value: string) => {
        setAssignKey((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!assignKey.name || !assignKey.description) {
            showError(t('common:requiredFields'));
            return new UnexpectedResult<OrganizationModel>();
        }
        if (!licenseKeyAssign?.originatedByOrganizationId || !assignKey) {
            showError(t('common:saveError'));
            return new UnexpectedResult<OrganizationModel>();
        }
        try {
            handleLoadingDataChange('managedKeys', true);

            const result = await createManagedOrg(licenseKeyAssign?.originatedByOrganizationId, assignKey);
            if (result.resultType === 'Ok' && result.data) {
                showSuccess(t('common:saved'));
            } else {
                showError(t('common:saveError'));
            }
            return result;
        } catch (error) {
            showError(t('common:saveError'));
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            handleLoadingDataChange('managedKeys', false);
        }
    };

    return {
        assignKey,
        licenseKeyAssign,
        setLicenseKeyAssign,
        resetAssignKey,
        handleInputChange,
        handleSave,
        isLoading: loadingData.managedOrgs,
    };
}
